import React, { useContext, useState } from 'react';
import { OrderJob } from '../../../../../redux/slices/slice_types';
import { Box, Grid, IconButton, Tooltip, Typography, lighten } from '@mui/material';
import PolarisIcon from '../../../../_common/PolarisIcon';
import styled from '@emotion/styled';
import { JobTypeLabel, TableJobStage, LabelValue, DateTimeDisplay } from '@/exports/columnCommonComponents';
import { ConfigContext } from '@/contextAPI/configContext';
import axios from '@/_utils/axios';
import { useToast } from '@/components/_common/Toast';
import { Dialog } from '@/components/_common';
import { StatusColors } from '@/constants/constants';
import { useAppSelector } from '@/redux/hooks';
import { selectJobCancelBulkAction } from '@/redux/selectors/dataTableSelectors';
import { selectUser } from '@/redux/selectors/authSelectors';

type BulkSearchJobProps = {
  job: OrderJob;
  submitting?: boolean;
  type?: boolean;
  orderId: string | number;
  updateCallback?: () => void;
};

const BulkSearchJob = ({ job, submitting = false, ...props }: BulkSearchJobProps) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { bulkOrderJobDisplay, getLineageMetaTitle } = useContext(ConfigContext).orderPlacementConfig ?? { orderPlacementConfig: {} };
  const { success, error } = useToast();
  const jobBulkCancel = useAppSelector(selectJobCancelBulkAction);
  const user = useAppSelector(selectUser);

  const onJobSubmit = () => {
    setSubmitLoading(true);
    axios({
      method: 'post',
      baseURL: '/api/v2/core/',
      url: `/orders/${(props as { orderId: number }).orderId}/bulk-submit/`,
      data: { job_ids: [job.id] }
    })
      .then(() => {
        setTimeout(() => {
          (props as { updateCallback: () => void }).updateCallback();
          success('Job submitted successfully');
        }, 300);
      })
      .catch(() => {
        error('Failed to submit job');
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const onJobCancel = () => {
    setCancelLoading(true);
    if (jobBulkCancel) {
      axios({
        method: 'post',
        url: `bulk_actions/${jobBulkCancel.action_type}/${jobBulkCancel.ident}/`,
        data: { resource_identifiers: [job.id], status_reason: `Cancelled by ${user.email || 'user'}` },
        baseURL: `/api/v${jobBulkCancel?.version || 1}/`
      })
        .then(() => {
          success('Job cancelled successfully');
          setTimeout(() => {
            (props as { updateCallback: () => void }).updateCallback();
          }, 300);
        })
        .catch(() => error('Failed to cancel job'));
    } else {
      axios({ method: 'post', baseURL: '/api/v1/core/', url: `/jobs/${job.id}/cancel/` })
        .then(() => {
          success('Job cancelled successfully');
          setTimeout(() => {
            (props as { updateCallback: () => void }).updateCallback();
          }, 300);
        })
        .catch(() => error('Failed to cancel job'));
    }
  };

  const defaultJobDisplay = (job: OrderJob) => (
    <DefaultWrapper>
      <Typography variant="h2" sx={{ fontSize: '1.2rem', fontWeight: 'normal' }}>
        <strong>{job.hid}</strong>
      </Typography>
      <JobTypeLabel type={job.job_type} />
      <Typography>{job.description}</Typography>
      <Grid sx={{ ml: 'auto' }} item sm={1.5}>
        <LabelValue label="Due Date">
          <DateTimeDisplay missingText="-" singleLine>
            {job.due_date}
          </DateTimeDisplay>
        </LabelValue>
      </Grid>
    </DefaultWrapper>
  );

  return (
    <JobRow
      key={job.id}
      onClick={(e) => {
        window.open(`${window.origin}/jobs/${job.id}`, '_blank');
        e.stopPropagation();
      }}
      style={{
        opacity: cancelLoading || submitLoading || submitting ? 0.5 : 1,
        pointerEvents: cancelLoading || submitLoading || submitting ? 'none' : 'auto'
      }}
    >
      <Grid container alignItems="center" sx={{ p: 1 }} columnSpacing={3}>
        <Grid item sx={{ flexGrow: 2 }}>
          {bulkOrderJobDisplay ? bulkOrderJobDisplay(job, getLineageMetaTitle(job.title)) : defaultJobDisplay(job)}
        </Grid>
        {props.type ? (
          <Grid item xs="auto" sx={{ pr: 2 }}>
            <TableJobStage
              enableWrap
              status={job.status === 'cancelled' ? job.status : job.stage || job.status}
              colorOverride={StatusColors[job.status as keyof typeof StatusColors]}
            />
          </Grid>
        ) : (
          <Box>
            <Tooltip title="Cancel Job">
              <IconButton
                size="small"
                sx={{ opacity: 0.5, mr: 1.5 }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowDialog(true);
                }}
              >
                <PolarisIcon icon={cancelLoading ? 'loading' : 'cancel'} height={cancelLoading ? 24 : 20} width={24} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Submit Job">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onJobSubmit();
                }}
              >
                <PolarisIcon icon={submitLoading || submitting ? 'loading' : 'send'} height={26} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Grid>
      <Dialog
        title="Cancel Job"
        titleIcon={<PolarisIcon icon="cancel" height={19} style={{ opacity: 0.5, marginRight: 8 }} />}
        open={showDialog}
        submitText="Cancel Job"
        cancelText="Close"
        buttonColor="error"
        onSubmit={() => {
          setShowDialog(false);
          onJobCancel();
        }}
        onCancel={() => setShowDialog(false)}
        maxWidth={400}
      >
        <DialogText>Are you sure you want to cancel job {job.hid}?</DialogText>
      </Dialog>
    </JobRow>
  );
};

export default BulkSearchJob;

const JobRow = styled.div`
  display: block;
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  text-decoration: none;
  color: ${({ theme }) => theme.palette.text.primary};
  background: ${({ theme }) => (theme.palette.mode === 'dark' ? lighten(theme.palette.background.paper, 0.07) : theme.palette.grey[200])};

  &:hover {
    text-decoration: none;
    background: ${({ theme }) =>
      `linear-gradient(50deg, ${lighten(theme.palette.background.paper, 0.05)} 0%, ${lighten(theme.palette.background.paper, 0.1)} 100%)`};
  }
`;

const DefaultWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  column-gap: ${({ theme }) => theme.spacing(2)};
`;

const DialogText = styled.div`
  word-wrap: break-word;
  word-break: break-all;
  hyphens: auto;
`;
